import axios from "axios";

const baseURL = process.env.VUE_APP_API_URL + process.env.VUE_APP_API_VERSION;
const axios_timeout = process.env.VUE_APP_AXIOS_TIMEOUT;

export const API = axios.create({

  baseURL: baseURL,
  timeout: axios_timeout,
  headers: {

    "Content-Type": "application/json;charset=UTF-8"
  }

});

// Füge globalen Response-Interceptor hinzu, um bei 401/419 sauber auszuloggen und umzuleiten
import router from "../router";
import store from "../store";
import { handleRegistrationError } from "@/plugins/store/errorHandlerSwal";
import { getTokens, setTokens, clearTokens } from "@/plugins/authTokens";

// Request Interceptor - Loader starten
API.interceptors.request.use(
  (config) => {
    // Loading State auf true setzen
    store.commit("GeneralApp/SET_LOADING", true);
    return config;
  },
  (error) => {
    // Bei Fehler Loader stoppen
    store.commit("GeneralApp/SET_LOADING", false);
    return Promise.reject(error);
  }
);

// Refresh-Token: Access-Token läuft nach 12 h ab, dann erneuern statt abzumelden.
// Pro Kontext läuft im Tab immer nur ein Refresh (Rotation: jeder Refresh-Token gilt nur einmal),
// alle parallel in 401 gelaufenen Requests warten auf dasselbe Promise.
// Andere Tabs teilen sich die Tokens über authTokens.js: Hat einer schon erneuert, wird
// dessen Token übernommen. Einen alten Refresh-Token wertet das Backend als Diebstahl.
const refreshPromises = { user: null, club: null };

function sentToken(config) {
  const headers = config.headers || {};
  const header = typeof headers.get === "function" ? headers.get("Authorization") : headers.Authorization;
  return String(header || "").replace(/^Bearer\s+/, "");
}

function authContext(config) {
  const sent = sentToken(config);
  if (sent) {
    if (sent === store.state.ClubAuth.Clubtoken || sent === getTokens("club").access_token) return "club";
    if (sent === store.state.UserAuth.token || sent === getTokens("user").access_token) return "user";
  }
  return /^\/?club\//.test(config.url || "") ? "club" : "user";
}

function isAuthRequest(url) {
  return /\/?(user|club)\/(login|logout|token\/refresh)$/.test(url || "");
}

function useToken(context, token) {
  store.commit(`${context === "club" ? "ClubAuth" : "UserAuth"}/SET_TOKEN`, token);
  localStorage.setItem("token", token);
  API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  return token;
}

// Token, den ein anderer Tab inzwischen geholt hat (sonst null)
function newerToken(context, sent) {
  const stored = getTokens(context).access_token;
  return stored && stored !== sent ? stored : null;
}

function refreshToken(context, sent) {
  if (!refreshPromises[context]) {
    refreshPromises[context] = (async () => {
      const newer = newerToken(context, sent);
      if (newer) return useToken(context, newer);

      const current = getTokens(context).refresh_token;
      if (!current) throw new Error("Kein Refresh-Token vorhanden");

      try {
        const resp = await axios.post(`${context}/token/refresh`, { refresh_token: current }, { baseURL, timeout: axios_timeout });
        setTokens(context, resp.data.access_token, resp.data.refresh_token);
        return useToken(context, resp.data.access_token);
      } catch (e) {
        // Ein anderer Tab war im selben Moment schneller: kurz warten, dann dessen Token nehmen
        if (e.response && e.response.status === 401) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const later = newerToken(context, sent);
          if (later) return useToken(context, later);
        }
        throw e;
      }
    })().finally(() => {
      refreshPromises[context] = null;
    });
  }
  return refreshPromises[context];
}

// Response Interceptor - Loader stoppen
let isHandlingUnauthorized = false;
API.interceptors.response.use(
  (response) => {
    // Bei erfolgreicher Response Loader stoppen
    store.commit("GeneralApp/SET_LOADING", false);
    store.commit("GeneralApp/SET_BACKEND_REACHABLE", true);
    return response;
  },
  (error) => {
    // Bei Fehler Loader stoppen
    store.commit("GeneralApp/SET_LOADING", false);


    if (!error || !error.response) {
      // Kein Response = Backend nicht erreichbar
      store.commit("GeneralApp/SET_BACKEND_REACHABLE", false);
      handleRegistrationError(error);
      return Promise.reject(error);
    }

    // Backend hat geantwortet → erreichbar
    store.commit("GeneralApp/SET_BACKEND_REACHABLE", true);

    const status = error.response.status;
    const config = error.config;

    if (status === 401 && config && !config._refreshRetry && !isAuthRequest(config.url)) {
      return refreshToken(authContext(config), sentToken(config))
        .then(token => {
          config._refreshRetry = true;
          config.headers.Authorization = `Bearer ${token}`;
          return API(config);
        })
        .catch(refreshError => {
          // Fehler des wiederholten Requests selbst durchreichen (dort ist der Interceptor schon gelaufen)
          if (refreshError && refreshError.config && refreshError.config._refreshRetry) {
            return Promise.reject(refreshError);
          }
          // Backend während des Refresh nicht erreichbar → nicht abmelden, nächster Request versucht es erneut
          if (refreshError && refreshError.isAxiosError && !refreshError.response) {
            store.commit("GeneralApp/SET_BACKEND_REACHABLE", false);
            return Promise.reject(error);
          }
          return handleUnauthorized(error);
        });
    }

    if (status === 401 || status === 419) {
      return handleUnauthorized(error);
    }

    return Promise.reject(error);
  }
);

function handleUnauthorized(error) {
  // Kiosk-Monitor (/club/today_monitor/…): nicht abmelden, today.vue meldet sich per QR-Token still neu an
  if (router.currentRoute && router.currentRoute.name === "/club/today_monitor") {
    return Promise.reject(error);
  }

  if (!isHandlingUnauthorized) {
    isHandlingUnauthorized = true;

    try {
      // Token entfernen
      localStorage.removeItem("token");
      clearTokens("user");
      clearTokens("club");
      delete API.defaults.headers.common["Authorization"];

      // Stores bereinigen (ohne API-Logout aufzurufen)
      try {
        store.commit("UserAuth/logout");
      } catch (e) {
        console.warn("UserAuth/logout commit fehlgeschlagen:", e);
      }
      try {
        store.commit("ClubAuth/logout");
      } catch (e) {
        console.warn("ClubAuth/logout commit fehlgeschlagen:", e);
      }

      try {
        store.commit("UserApp/set_appDataClear", null, { root: true });
      } catch (e) {
        console.warn("UserApp/set_appDataClear fehlgeschlagen:", e);
      }
      try {
        store.commit("ClubApp/set_appDataClear", null, { root: true });
      } catch (e) {
        console.warn("ClubApp/set_appDataClear fehlgeschlagen:", e);
      }
    } finally {
      const currentPath = router.currentRoute && router.currentRoute.fullPath ? router.currentRoute.fullPath : "/";
      const alreadyOnLogin = router.currentRoute && router.currentRoute.path === "/login";

      const resetFlag = () => {
        isHandlingUnauthorized = false;
      };

      if (!alreadyOnLogin) {
        router.replace({ path: "/login", query: { redirect: currentPath } })
          .finally(resetFlag);
      } else {
        resetFlag();
      }
    }
  }

  return Promise.reject(error);
}
